import { FC, ReactNode, startTransition, useContext, useEffect, useRef, useState } from "react";
import { DeferHiddenBlocksContext } from "@/components/deferred-mount/DeferredMount";
import { scheduleIdle } from "@/lib/defer";
import { useNearViewport } from "src/hooks/useNearViewport";

/**
 * `DeferredMount` with a sized placeholder: for a block that is not visible at first paint but
 * takes up room, so the page keeps its shape until the block is drawn. The front page's lower
 * panels each fetch and render a list; on a phone they sit a screen or more below the fold, on a
 * one-screen desktop they are an empty row until their data is in. Once shown they stay mounted.
 *
 * In a browser's render (`DeferHiddenBlocksContext`, see src/lib/home-render.ts) the children
 * are left out of the server HTML and of hydration, and drawn as a transition (time-sliced, not
 * one long task) once the placeholder comes within `margin` of the viewport, or, when `eager`,
 * once the browser is first idle (at most 2 s): the one-screen desktop layout shows the row from
 * the start, so it does not wait to be scrolled to. Neither before `hold` is false. A crawler's
 * full render, and any page without the browser/crawler split, draws the children on the server
 * as before.
 *
 * The placeholder takes `className` and `placeholderClassName`, and its size comes from CSS, so
 * each layout reserves what it needs: a fixed height on a phone, nothing extra on a desktop where
 * the row's height comes from the flex layout. A fixed height there squeezed the row above until
 * the children mounted, which then moved down by 230 px (#1351).
 */
const WhenNear: FC<{ eager?: boolean; hold?: boolean; margin?: string; className?: string; placeholderClassName?: string; children: ReactNode }> = ({ eager = false, hold = false, margin = "600px", className, placeholderClassName, children }) => {
    const defer = useContext(DeferHiddenBlocksContext);
    const [shown, setShown] = useState(!defer);
    const ref = useRef<HTMLDivElement>(null);
    // Not watched while eager: on the one-screen layout the row is in view at once, and showing it
    // on that first frame would put its render back into the start-up work idle lets it wait for.
    // Rooted at the box that scrolls the row (on a phone, the module's content box), so `margin`
    // counts: rooted at the page, the row mounted only once on screen and grew there (#1355).
    const near = useNearViewport(ref, { margin, enabled: !shown && !eager, scroller: "nearest" });

    useEffect(() => {
        // `hold`: not yet, whatever else says so (the lower row waits to know which panels it will
        // hold, so none is drawn and then taken away). A crawler's full render is never held.
        if (shown || hold) return;
        const show = () => startTransition(() => setShown(true));
        if (near) {
            show();
            return;
        }
        if (eager) return scheduleIdle(show);
    }, [shown, hold, eager, near]);

    return shown ? <div className={className}>{children}</div> : <div ref={ref} className={[className, placeholderClassName].filter(Boolean).join(" ")} aria-hidden="true" />;
};

export default WhenNear;
